// client/src/components/secure/training/slideLogic.js — pure helpers behind the slide-video
// player and the flash-card deck (tested by scripts/test-training-videos.js, which loads this
// file by stripping `export`; keep every helper a plain `export function`).

/** Spoken length of a text in seconds at a speech rate (150 words a minute at 1.0). */
export function estimateSeconds(text, rate) {
    const n = String(text || '').trim().split(/\s+/).filter(Boolean).length;
    const r = Number(rate) > 0 ? Number(rate) : 1;
    return Math.max(2, Math.round((n / (150 * r)) * 60 * 10) / 10);
}

/** Narration split into caption-sized sentences (also keeps each utterance short). */
export function splitSentences(text) {
    const t = String(text || '').replace(/\s+/g, ' ').trim();
    if (!t) return [];
    const parts = t.match(/[^.!?]+(?:[.!?]+["'”’)]*|$)/g) || [t];
    return parts.map((s) => s.trim()).filter(Boolean);
}

export function clampIndex(i, n) {
    if (!n) return 0;
    return Math.min(Math.max(0, Number(i) || 0), n - 1);
}

/** Percent through the deck, counting the slide on screen. */
export function progressPercent(i, n) {
    if (!n) return 0;
    return Math.round(((clampIndex(i, n) + 1) / n) * 100);
}

/** Slides seen so far, as a sorted list without repeats. */
export function markSeen(seen, i) {
    const s = new Set(seen || []);
    s.add(Number(i));
    return [...s].sort((a, b) => a - b);
}

/** Complete when the last slide was reached or at least 90% of the slides were viewed. */
export function isPlayerComplete(seen, total) {
    if (!total) return false;
    const list = seen || [];
    return list.includes(total - 1) || list.length / total >= 0.9;
}

/** An en-US voice: local en-US first, then any en-US, then any English, else null. */
export function pickVoice(voices, lang) {
    const want = (lang || 'en-US').toLowerCase();
    const list = (voices || []).filter(Boolean);
    const norm = (v) => String(v.lang || '').toLowerCase().replace('_', '-');
    return list.find((v) => norm(v) === want && v.localService)
        || list.find((v) => norm(v) === want)
        || list.find((v) => norm(v).startsWith('en'))
        || null;
}

/** A finished touch gesture: 'next' (swipe left), 'prev' (swipe right) or null. */
export function swipeDirection(dx, dy, min) {
    const m = min || 50;
    if (Math.abs(dx) < m || Math.abs(dx) < Math.abs(dy) * 1.5) return null;
    return dx < 0 ? 'next' : 'prev';
}

/** Keyboard shortcuts for the slide player. */
export function slideKeyAction(key) {
    switch (key) {
    case 'ArrowRight': case 'PageDown': case 'n': return 'next';
    case 'ArrowLeft': case 'PageUp': case 'p': return 'prev';
    case ' ': case 'k': case 'Spacebar': return 'toggle';
    case 'Home': return 'first';
    case 'End': return 'last';
    case 'c': return 'captions';
    case 't': return 'transcript';
    default: return null;
    }
}

/**
 * The screenshot to show on a slide: the signed link the server added (image_src, a
 * same-site /training-media/ path) or an https:// address. Anything else (a bare file name,
 * javascript:, data:) shows no picture.
 */
export function slideImageSrc(slide) {
    const s = slide && typeof slide.image_src === 'string' ? slide.image_src.trim() : '';
    if (/^\/training-media\/[^\s"'<>]+$/.test(s) || /^https:\/\/[^\s"'<>]+$/.test(s)) return s;
    return null;
}

/** The recorded narration for a slide (audio_src, same rules as the screenshot), or null. */
export function slideAudioSrc(slide) {
    const s = slide && typeof slide.audio_src === 'string' ? slide.audio_src.trim() : '';
    if (/^\/training-media\/[^\s"'<>]+$/.test(s) || /^https:\/\/[^\s"'<>]+$/.test(s)) return s;
    return null;
}

/**
 * How a slide is narrated: 'audio' (its recorded MP3), 'speech' (the browser's voice) or
 * 'timer' (silent, captions only). Voice off → timer. A slide whose audio failed to load
 * (audioFailed) falls back to speech, then timer.
 */
export function narrationMode(slide, voiceOn, canSpeak, audioFailed) {
    if (!voiceOn) return 'timer';
    if (slideAudioSrc(slide) && !audioFailed) return 'audio';
    return canSpeak ? 'speech' : 'timer';
}

/** Any slide with recorded narration? (then "Voice" works even where the browser cannot speak) */
export function hasRecordedNarration(slides) {
    return (slides || []).some((s) => !!slideAudioSrc(s));
}

/** A playback speed the audio element accepts (0.5–2, default 1). */
export function safeRate(rate) {
    const r = Number(rate);
    return r >= 0.5 && r <= 2 ? r : 1;
}

/** The next slide's screenshot, if any (preloaded while this slide plays). */
export function nextImageSrc(slides, i) {
    const list = slides || [];
    return list.length && i + 1 < list.length ? slideImageSrc(list[i + 1]) : null;
}

// ---- Flash cards: a simple in-session spaced repeat ---------------------------------
// The deck is a queue of card indices still to learn. "Got it" takes the card out; "Again"
// puts it back a few places later so it comes round again soon. Done = every card got once.

export function deckInit(n) {
    return { queue: Array.from({ length: Math.max(0, n) }, (_, i) => i), pos: 0, flipped: false, got: [], again: 0, total: Math.max(0, n) };
}

export function deckCurrent(s) {
    return s.queue.length ? s.queue[Math.min(s.pos, s.queue.length - 1)] : null;
}

export function deckFlip(s) {
    return { ...s, flipped: !s.flipped };
}

export function deckNext(s) {
    if (!s.queue.length) return s;
    return { ...s, pos: (s.pos + 1) % s.queue.length, flipped: false };
}

export function deckPrev(s) {
    if (!s.queue.length) return s;
    return { ...s, pos: (s.pos - 1 + s.queue.length) % s.queue.length, flipped: false };
}

export function deckGot(s) {
    if (!s.queue.length) return s;
    const card = deckCurrent(s);
    const queue = s.queue.filter((c) => c !== card);
    const got = s.got.includes(card) ? s.got : [...s.got, card];
    return { ...s, queue, got, pos: queue.length ? Math.min(s.pos, queue.length - 1) : 0, flipped: false };
}

export function deckAgain(s, gap) {
    if (!s.queue.length) return s;
    const g = gap || 3;
    const card = deckCurrent(s);
    const rest = s.queue.filter((c) => c !== card);
    if (rest.length && s.pos >= rest.length) {          // it was the last card: start again from the top
        return { ...s, queue: [...rest, card], pos: 0, flipped: false, again: s.again + 1 };
    }
    const at = Math.min(s.pos + g, rest.length);
    const queue = [...rest.slice(0, at), card, ...rest.slice(at)];
    const pos = queue.length === 1 ? 0 : Math.min(s.pos, queue.length - 1);
    return { ...s, queue, pos, flipped: false, again: s.again + 1 };
}

export function deckShuffle(s, rng) {
    const r = rng || Math.random;
    const q = [...s.queue];
    for (let i = q.length - 1; i > 0; i -= 1) {
        const j = Math.floor(r() * (i + 1));
        const t = q[i]; q[i] = q[j]; q[j] = t;
    }
    return { ...s, queue: q, pos: 0, flipped: false };
}

export function deckDone(s) {
    return s.total > 0 && s.queue.length === 0;
}

/** Keyboard shortcuts for the deck. */
export function deckKeyAction(key) {
    switch (key) {
    case ' ': case 'Enter': case 'f': case 'Spacebar': return 'flip';
    case 'ArrowRight': return 'next';
    case 'ArrowLeft': return 'prev';
    case 'g': case 'ArrowUp': return 'got';
    case 'a': case 'ArrowDown': return 'again';
    case 's': return 'shuffle';
    default: return null;
    }
}
